import React from 'react';
import { Element } from 'react-scroll';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  TrashIcon,
  CaretDownIcon,
  CaretUpIcon,
  WarningCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowCounterClockwiseIcon,
  CopyIcon,
} from '@phosphor-icons/react';
import { DivisionSimpleDTO, RebateRule } from '@erp/shared-types';
import { ProductGroup } from 'src/api/rebates/useProductGroups';
import { formatCurrency, formatPercentage } from '@erp/shared-utils';
import { capitalizeWords } from 'src/utils/strings';
import { RebateRuleInlineForm } from 'src/components/rebates/RebateRuleInlineForm';
import {
  getStatusStyle,
  getStatusLabel,
  type ChangeStatus,
} from 'src/components/common/ChangeStatus';
import { ManualAmount } from 'src/api/rebates/useManualAmounts';

interface RuleCardEditableProps {
  rule: RebateRule;
  isExpanded: boolean;
  onToggleExpansion: () => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  onUpdate?: (ruleId: string, updates: RebateRule) => void;
  onValidationChange?: (ruleId: string, isValid: boolean) => void;
  isValid?: boolean;
  divisions: DivisionSimpleDTO[];
  productGroups: ProductGroup[];
  agreementStatus: 'draft' | 'finalized';
  hasErrors: boolean;
  validationErrors: string[];
  changeStatus?: ChangeStatus;
  deleteIcon?: React.ComponentType<{ className?: string }>;
  deleteTooltip?: string;
  // Manual amounts data (bulk fetched from parent)
  manualAmountsByRuleId?: Map<string, ManualAmount[]>;
  // Total rebate amount for this rule
  rebateTotal?: number;
  // Tier status by type (estimates, predictions)
  tierStatus?: { estimates?: number; predictions?: number; reported?: number };
  // Agreement dates for rule duration bounds
  agreementStartDate?: string;
  agreementEndDate?: string;
}

export const RuleCardEditable = React.memo<RuleCardEditableProps>(
  ({
    rule,
    isExpanded,
    onToggleExpansion,
    onDelete,
    onDuplicate,
    onUpdate,
    onValidationChange,
    isValid,
    divisions,
    productGroups,
    agreementStatus,
    hasErrors,
    validationErrors,
    changeStatus = 'unchanged',
    deleteIcon: CustomDeleteIcon = TrashIcon,
    deleteTooltip = 'Delete this rule',
    // Manual amounts data (bulk fetched from parent)
    manualAmountsByRuleId,
    // Total rebate amount for this rule
    rebateTotal,
    // Tier status by type (estimates, predictions)
    tierStatus,
    // Agreement dates for rule duration bounds
    agreementStartDate,
    agreementEndDate,
  }) => {
    const tierCount = rule.tiers?.length || 0;
    const divisionCount = rule.divisions?.length || 0;

    // Validation logic is now handled by parent component
    // hasErrors and validationErrors are passed as props

    // Get change status styling
    const statusStyle = getStatusStyle(changeStatus);
    const showStatusIndicator = changeStatus !== 'unchanged';
    const isRemoved = changeStatus === 'removed';

    // Get manual amounts count from bulk-fetched data
    const manualAmounts = manualAmountsByRuleId?.get(rule.id) || [];
    const manualAmountsCount = manualAmounts.length;

    return (
      <Element name={`rule-${rule.id}`}>
        <Card
          className={`border overflow-hidden transition-shadow duration-200 ${
            hasErrors ? 'border-red-300 bg-red-50/30' : ''
          } ${
            showStatusIndicator ? `${statusStyle.border} ${statusStyle.bg}` : ''
          } ${isRemoved ? 'opacity-60' : ''}`}
        >
          {/* Collapsed Header */}
          <div
            className="flex justify-between items-center p-6 cursor-pointer"
            onClick={onToggleExpansion}
          >
            <div className="flex items-center gap-4">
              {/* Status Indicator */}
              {showStatusIndicator && statusStyle.icon && (
                <div
                  className={`flex items-center justify-center w-7 h-7 rounded-full flex-shrink-0 ${statusStyle.iconBg}`}
                >
                  {statusStyle.icon}
                </div>
              )}

              <div>
                <div className="flex items-center gap-2 mb-1">
                  {/* Validation status icon */}
                  {hasErrors ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <XCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>This rule has validation errors</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
                  )}

                  <h3 className="text-lg font-medium">{rule.name}</h3>
                  {rebateTotal !== undefined && (
                    <Badge
                      variant="outline"
                      className={
                        rebateTotal >= 0
                          ? 'bg-green-50 text-green-700 border-green-200'
                          : 'bg-red-50 text-red-700 border-red-200'
                      }
                    >
                      {formatCurrency(rebateTotal)}
                    </Badge>
                  )}
                  {showStatusIndicator && (
                    <Badge
                      variant="outline"
                      className={`${statusStyle.textColor} border-current`}
                    >
                      {getStatusLabel(changeStatus)}
                    </Badge>
                  )}
                  {validationErrors.length > 0 && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge
                            variant="destructive"
                            className="bg-red-100 text-red-800 hover:bg-red-200"
                          >
                            <WarningCircleIcon className="h-3 w-3 mr-1" />
                            {validationErrors.length} issue
                            {validationErrors.length > 1 ? 's' : ''}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="space-y-1">
                            {validationErrors.map((error, idx) => (
                              <p key={idx}>• {error}</p>
                            ))}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
                <div className="flex gap-2 items-center flex-wrap">
                  {rule.isManual ? (
                    <>
                      <Badge
                        variant="outline"
                        className="border-yellow-200 text-yellow-700"
                      >
                        Manual
                      </Badge>
                      {manualAmountsCount > 0 && (
                        <Badge
                          variant="outline"
                          className="border-blue-200 text-blue-700"
                        >
                          {manualAmountsCount} payment
                          {manualAmountsCount !== 1 ? 's' : ''}
                        </Badge>
                      )}
                    </>
                  ) : (
                    <>
                      {rule.thresholdType && (
                        <Badge
                          variant="outline"
                          className={
                            rule.thresholdType === 'volume'
                              ? 'border-blue-200 text-blue-700'
                              : rule.thresholdType === 'growth'
                                ? 'border-green-200 text-green-700'
                                : 'border-gray-200 text-gray-700'
                          }
                        >
                          {capitalizeWords(rule.thresholdType)}
                        </Badge>
                      )}
                    </>
                  )}
                  <span className="text-sm text-muted-foreground ml-2">
                    {tierCount} tier{tierCount !== 1 ? 's' : ''} •{' '}
                    {divisionCount} division
                    {divisionCount !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>

            <div
              className="flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleExpansion();
                }}
                className="h-8 w-8 p-0"
              >
                {isExpanded ? (
                  <CaretUpIcon className="h-4 w-4" />
                ) : (
                  <CaretDownIcon className="h-4 w-4" />
                )}
              </Button>

              {onDuplicate && !isRemoved && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDuplicate();
                        }}
                        className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700"
                      >
                        <CopyIcon className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Duplicate this rule</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              {onDelete && !isRemoved && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete();
                        }}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                      >
                        <CustomDeleteIcon className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{deleteTooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>

          {/* Inline Edit Form - Always available when expanded in edit mode */}
          {isExpanded && (
            <CardContent className="pt-0 px-6 pb-6">
              {isRemoved ? (
                <div className="text-center py-4 text-muted-foreground">
                  This rule is marked for deletion
                </div>
              ) : (
                <RebateRuleInlineForm
                  rule={rule}
                  divisions={divisions}
                  productGroups={productGroups}
                  onUpdate={(updates) => {
                    onUpdate?.(rule.id, updates);
                  }}
                  onValidationChange={(isValid) => {
                    onValidationChange?.(rule.id, isValid);
                  }}
                  tierStatus={tierStatus}
                  agreementStartDate={agreementStartDate}
                  agreementEndDate={agreementEndDate}
                />
              )}
            </CardContent>
          )}
        </Card>
      </Element>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if rule, isExpanded, or changeStatus changed
    return (
      prevProps.rule.id === nextProps.rule.id &&
      JSON.stringify(prevProps.rule) === JSON.stringify(nextProps.rule) &&
      prevProps.isExpanded === nextProps.isExpanded &&
      prevProps.changeStatus === nextProps.changeStatus &&
      JSON.stringify(prevProps.productGroups) ===
        JSON.stringify(nextProps.productGroups) &&
      JSON.stringify(prevProps.divisions) ===
        JSON.stringify(nextProps.divisions)
    );
  },
);

RuleCardEditable.displayName = 'RuleCardEditable';
